import type {
  AspectoBotella,
  FormaSello,
} from '#components/mobile/budget/recuerdosAspecto';

/**
 * Geometría de la botella, el corcho, el lacre y el sello (funciones puras,
 * sin React). Es la del prototipo aprobado (`prototipo-botella.html`:
 * `silueta`, `svgVidrio`, `svgCorcho`, `svgLacre`, `svgSello`) para que la
 * app pinte la misma pieza. Lienzo de 120 de ancho y `alto + 6` de alto.
 *
 * Nada de `Math.random()`: las irregularidades (burbujas, poros del corcho,
 * borde del sello, chispa) salen de la semilla del nombre.
 */

/** Ancho del lienzo de la botella. */
export const ANCHO_BOTELLA = 120;
/** Altura del labio del cuello en el lienzo. */
export const LABIO = 14;

/** Generador determinista (mulberry32, el `rnd` del prototipo). */
export function aleatorio(semilla: number): () => number {
  let s = semilla | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function f(n: number): string {
  return n.toFixed(1).replace(/\.0$/, '');
}

export type Cotas = {
  /** Alto del lienzo (alto de la botella + 6). */
  lienzo: number;
  alto: number;
  izq: number;
  der: number;
  cuelloIzq: number;
  cuelloDer: number;
  /** Fin del cuello. */
  baseCuello: number;
  /** Fin del hombro (empieza el cuerpo recto). */
  baseHombro: number;
};

export function cotas(a: AspectoBotella): Cotas {
  const m = a.medidas;
  const baseCuello = LABIO + 9.5 + m.altoCuello;
  return {
    lienzo: m.alto + 6,
    alto: m.alto,
    izq: 60 - m.anchoCuerpo / 2,
    der: 60 + m.anchoCuerpo / 2,
    cuelloIzq: 60 - m.anchoCuello / 2,
    cuelloDer: 60 + m.anchoCuello / 2,
    baseCuello,
    baseHombro: baseCuello + m.hombro,
  };
}

/** Silueta de la botella (labio, cuello, hombro, cuerpo y culo redondeado). */
export function silueta(a: AspectoBotella): string {
  const c = cotas(a);
  const sh = a.medidas.hombro;
  const lt = LABIO;
  const { izq: L, der: R, cuelloIzq: nl, cuelloDer: nr } = c;
  const nb = c.baseCuello;
  const sb = c.baseHombro;
  const H = c.alto;
  return [
    `M${f(nl - 3)} ${f(lt + 1.5)}Q${f(nl - 3)} ${lt} ${f(nl - 1.5)} ${lt}`,
    `L${f(nr + 1.5)} ${lt}Q${f(nr + 3)} ${lt} ${f(nr + 3)} ${f(lt + 1.5)}`,
    `L${f(nr + 3)} ${lt + 7}Q${f(nr + 3)} ${lt + 9} ${f(nr)} ${f(lt + 9.5)}`,
    `L${f(nr)} ${f(nb)}C${f(nr)} ${f(nb + sh * 0.55)} ${f(R)} ${f(nb + sh * 0.3)} ${f(R)} ${f(sb)}`,
    `L${f(R)} ${f(H - 10)}Q${f(R)} ${H} ${f(R - 10)} ${H}`,
    `L${f(L + 10)} ${H}Q${f(L)} ${H} ${f(L)} ${f(H - 10)}`,
    `L${f(L)} ${f(sb)}C${f(L)} ${f(nb + sh * 0.3)} ${f(nl)} ${f(nb + sh * 0.55)} ${f(nl)} ${f(nb)}`,
    `L${f(nl)} ${f(lt + 9.5)}Q${f(nl - 3)} ${lt + 9} ${f(nl - 3)} ${lt + 7}Z`,
  ].join('');
}

export type Burbuja = { cx: number; cy: number; r: number };

/** Burbujas del vidrio (7-11, sembradas con el nombre). */
export function burbujas(a: AspectoBotella): Burbuja[] {
  const c = cotas(a);
  const r = aleatorio(a.semilla);
  const n = 7 + (a.semilla % 5);
  const ancho = a.medidas.anchoCuerpo;
  return Array.from({ length: n }, () => ({
    cx: +(c.izq + 8 + r() * (ancho - 16)).toFixed(1),
    cy: +(c.baseHombro + 6 + r() * (c.alto - c.baseHombro - 16)).toFixed(1),
    r: +(0.6 + r() * 1.1).toFixed(1),
  }));
}

/** Marcas grabadas en el cuello: una raya por aportación (≤ 24). */
export function marcasCuello(a: AspectoBotella): string {
  const c = cotas(a);
  const paso = Math.min(4, (a.medidas.altoCuello - 8) / Math.max(a.marcas, 1));
  let d = '';
  for (let i = 0; i < a.marcas; i++) {
    const y = LABIO + 14 + i * paso;
    d += `M${f(c.cuelloIzq + 3)} ${f(y)}h${f(a.medidas.anchoCuello * 0.42)}`;
  }
  return d;
}

/** Rollo de la carta dentro de la botella (grosor según el largo). */
export function rolloInterior(a: AspectoBotella): {
  x: number;
  y: number;
  ancho: number;
  alto: number;
} {
  const c = cotas(a);
  return {
    x: 60 - a.rollo / 2 - 6,
    y: c.baseHombro - 6,
    ancho: a.rollo,
    alto: c.alto - c.baseHombro - 4,
  };
}

/** Poros del corcho. */
export function porosCorcho(a: AspectoBotella): Burbuja[] {
  const c = cotas(a);
  const ancho = a.medidas.anchoCuello - 3;
  const r = aleatorio(a.semilla + 7);
  return Array.from({ length: 12 }, () => ({
    cx: +(c.cuelloIzq + 2 + r() * (ancho - 2)).toFixed(1),
    cy: +(LABIO - 8 + r() * 20).toFixed(1),
    r: 0.55,
  }));
}

/** Gota de lacre que cubre el corcho y chorrea por el cuello. */
export function lacre(a: AspectoBotella): string {
  const c = cotas(a);
  const lt = LABIO;
  const nl = c.cuelloIzq;
  const nr = c.cuelloDer;
  return [
    `M${f(nl - 5)} ${lt + 6}C${f(nl - 6)} ${lt - 4} ${f(nl - 2)} ${lt - 12} 60 ${lt - 12}`,
    `C${f(nr + 2)} ${lt - 12} ${f(nr + 6)} ${lt - 4} ${f(nr + 5)} ${lt + 6}`,
    `L${f(nr + 4)} ${lt + 14}Q${f(nr + 2)} ${lt + 18} ${f(nr)} ${lt + 13}`,
    `L${f(nr - 4)} ${lt + 9}L${f(nr - 6)} ${lt + 20}Q${f(nr - 8)} ${lt + 24} ${f(nr - 10)} ${lt + 19}`,
    `L${f(nr - 11)} ${lt + 9}L${f(nl + 9)} ${lt + 9}L${f(nl + 7)} ${lt + 16}`,
    `Q${f(nl + 5)} ${lt + 20} ${f(nl + 3)} ${lt + 15}L${f(nl + 1)} ${lt + 9}`,
    `Q${f(nl - 4)} ${lt + 12} ${f(nl - 5)} ${lt + 6}Z`,
  ].join('');
}

/** Recorte de cada mitad del lacre (se parte en zigzag por el centro). */
export function corteLacre(lado: 'izq' | 'der'): string {
  const lt = LABIO;
  return lado === 'izq'
    ? `M0 -20H60L57 ${lt - 6}L62 ${lt}L58 ${lt + 6}L61 ${lt + 30}H0Z`
    : `M60 -20H120V${lt + 30}H61L58 ${lt + 6}L62 ${lt}L57 ${lt - 6}Z`;
}

/** Borde irregular del sello (18 puntos, sembrado con el nombre). */
export function bordeSello(semilla: number): string {
  const r = aleatorio(semilla + 3);
  const pts: [number, number][] = [];
  for (let i = 0; i < 18; i++) {
    const ang = (i / 18) * Math.PI * 2;
    const rr = 56 + r() * 5;
    pts.push([64 + Math.cos(ang) * rr, 64 + Math.sin(ang) * rr]);
  }
  let d = `M${f(pts[0][0])} ${f(pts[0][1])}`;
  for (let i = 1; i <= 18; i++) {
    const p = pts[i % 18];
    const q = pts[i - 1];
    d += `Q${f(q[0])} ${f(q[1])} ${f((p[0] + q[0]) / 2)} ${f((p[1] + q[1]) / 2)}`;
  }
  return `${d}Z`;
}

/** Contorno grabado del sello según su forma (lienzo 128 × 128). */
export function contornoSello(forma: FormaSello): string {
  switch (forma) {
    case 'circulo':
      return 'M20 64a44 44 0 1 0 88 0a44 44 0 1 0-88 0Z';
    case 'octogono':
      return 'M45.8 20h36.4L108 45.8v36.4L82.2 108H45.8L20 82.2V45.8Z';
    case 'cuadrado':
      return 'M28 22h72a6 6 0 0 1 6 6v72a6 6 0 0 1-6 6H28a6 6 0 0 1-6-6V28a6 6 0 0 1 6-6Z';
    case 'rombo':
      return 'M64 18 110 64 64 110 18 64Z';
    default:
      return 'M18 64a46 38 0 1 0 92 0a46 38 0 1 0-92 0Z';
  }
}

/** Radio del arco del texto del sello (cabe dentro del contorno). */
export const RADIO_TEXTO_SELLO = 31;

/** Avance medio de una mayúscula serif (em) en el arco del sello. */
const AVANCE_LETRA = 0.66;

/** Largo estimado del texto en arco (lienzo del sello). */
export function largoArco(
  caracteres: number,
  tamano: number,
  espaciado: number,
): number {
  return caracteres * (tamano * AVANCE_LETRA + espaciado);
}

/**
 * Tamaño de letra del texto en arco para que quepa en la circunferencia
 * (deja un 8 % de hueco entre el final y el principio). Baja de 8,2 a 5,6;
 * si ni así cabe, recorta el texto con «…».
 */
export function letraSello(texto: string): {
  tamano: number;
  espaciado: number;
  texto: string;
} {
  const letras = [...texto];
  const disponible = 2 * Math.PI * RADIO_TEXTO_SELLO * 0.92;
  for (let i = 0; i <= 13; i++) {
    const tamano = +(8.2 - i * 0.2).toFixed(1);
    const espaciado = tamano >= 8 ? 1.1 : tamano >= 7 ? 0.7 : 0.3;
    if (largoArco(letras.length, tamano, espaciado) <= disponible) {
      return { tamano, espaciado, texto };
    }
  }
  const caben = Math.floor(disponible / (5.6 * AVANCE_LETRA + 0.3)) - 1;
  return {
    tamano: 5.6,
    espaciado: 0.3,
    texto: `${letras.slice(0, caben).join('').trimEnd()}…`,
  };
}

/** Vector de cada rombo de la chispa: [dx, dy, escala]. */
export type Destello = readonly [number, number, number];

/** Chispa náutica: 8 rombos en abanico hacia arriba (+ 1 estrella aparte). */
export function chispa(semilla: number): Destello[] {
  const r = aleatorio(semilla + 1);
  return Array.from({ length: 8 }, (_, i) => {
    const ang = ((-160 + i * 20 + r() * 8) * Math.PI) / 180;
    const dist = 70 + r() * 60;
    return [
      +(Math.cos(ang) * dist).toFixed(1),
      +(Math.sin(ang) * dist).toFixed(1),
      +(0.6 + r() * 0.6).toFixed(2),
    ] as const;
  });
}

/** Líneas de rumbo del cielo (cartografía antigua), desde un punto del horizonte. */
export function rumbosCielo(cx: number, cy: number, largo = 620): string {
  let d = '';
  for (let i = 0; i < 9; i++) {
    const ang = Math.PI + (i * Math.PI) / 8;
    d += `M${cx} ${cy}L${f(cx + Math.cos(ang) * largo)} ${f(cy + Math.sin(ang) * largo)}`;
  }
  return d;
}

/** Reflejo del sol (o la luna) en el agua: rayas cortas que se estrechan. */
export function reflejoAstro(x: number): {
  x: number;
  y: number;
  ancho: number;
}[] {
  const r = aleatorio(11);
  return Array.from({ length: 14 }, (_, i) => {
    const y = 4 + i * 7 + r() * 3;
    const ancho = (26 - i * 1.4) * (0.6 + r() * 0.6);
    return {
      x: +(x - ancho / 2 + (r() - 0.5) * 8).toFixed(1),
      y: +y.toFixed(1),
      ancho: +ancho.toFixed(1),
    };
  });
}
